import React, { useEffect, useState, useCallback } from 'react';
import { message } from 'antd';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { ServicesService } from 'services/ServicesService';
import { useDrawers } from 'hooks/useDrawers';
import {
  REQUEST_LOADING,
  SUCCESS_DELETE,
  SUCCESS_UPDATE,
} from 'constants/alerts';
import { usePagination } from 'hooks/usePagination';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { priceRender } from 'utils/table';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { OptionsTable } from './OptionsTable';
import { OptionsForm } from './OptionsForm';

function Options() {
  useDocumentTitle('Опции');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { setCount } = useHeaderChanger('Опции');
  const {
    state: { drawerVisible, activeRecord, formIsActive },
    openDrawer,
    closeDrawer,
    openEditForm,
    closeForm,
  } = useDrawers();
  const { page, perPage, totalRows, setPagination, setTotalRows } =
    usePagination();

  const fetchOptions = useCallback(async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await ServicesService.getOptions({
        page,
        perPage,
      });
      setOptions(items);
      setCount(total);
      setTotalRows(total);
    } catch (err) {
      message.error('Не удалось загрузить опции');
    } finally {
      setLoading(false);
    }
  }, [page, perPage]);

  useEffect(() => fetchOptions(), [fetchOptions]);

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await ServicesService.deleteOption(activeRecord.id);

      if (isDeleted) {
        closeDrawer();
        fetchOptions();
        message.success(SUCCESS_DELETE);
      }
    } catch (err) {
      message.error('Не удалось удалить опцию');
    } finally {
      hide();
    }
  };

  const handleUpdate = async (data, id) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await ServicesService.updateOption(data, id);

      if (isUpdated) {
        closeForm();
        fetchOptions();
        message.success(SUCCESS_UPDATE);
      }
    } catch (err) {
      message.error('Не удалось отредактировать опцию');
    } finally {
      hide();
    }
  };

  return (
    <>
      <OptionsTable
        items={options}
        loading={loading}
        onRowClick={openDrawer}
        total={totalRows}
        current={page}
        pageSize={perPage}
        onChange={setPagination}
      />

      <OptionsForm
        isActive={formIsActive}
        onClose={closeForm}
        onUpdate={handleUpdate}
        initialValues={activeRecord}
      />

      <RecordInfoDrawer
        record={activeRecord}
        pageId="option"
        recordFields={[
          { label: 'Наименование', key: 'title' },
          { label: 'Наименование (каз)', key: 'title_kz' },
          {
            label: 'Цена',
            key: 'price',
            computedValue: `${priceRender(activeRecord?.price)} ₸`,
          },
          { label: 'Период', key: 'period' },
          { label: 'Скидка (%)', key: 'discount_percent' },
          {
            label: 'Промокод',
            key: 'withPromo',
            computedValue: activeRecord?.withPromo ? 'Да' : 'Нет',
          },
          {
            label: 'Доступен',
            key: 'isPublic',
            computedValue: activeRecord?.isPublic ? 'Да' : 'Нет',
          },
        ]}
        drawerTitle={activeRecord?.title}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        deleteConfirmText="Вы точно хотите удалить эту опцию?"
      />
    </>
  );
}

export { Options };
